import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Empty, Space, Spin, Tag } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { api } from "./api";
import { executionLabels, type ExecutionStatus, statusLabels, type TaskStatus } from "./domain";
export function useResource<T>(path: string | null) {
  const [data, setData] = useState<T>();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(path !== null);
  const request = useRef(0);
  const reload = useCallback(async () => {
    const id = ++request.current;
    if (path === null) {
      setData(undefined);
      setError("");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await api<T>(path);
      if (id === request.current) setData(result);
    } catch (e) {
      if (id === request.current) setError((e as Error).message);
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, [path]);
  useEffect(() => {
    void reload();
    return () => {
      request.current++;
    };
  }, [reload]);
  return { data, error, loading, reload };
}
export function Resource<T>({
  resource,
  children,
  empty = false,
}: {
  resource: ReturnType<typeof useResource<T>>;
  children: (data: T) => React.ReactNode;
  empty?: boolean;
}) {
  if (resource.error)
    return (
      <Alert
        type="error"
        showIcon
        message="数据暂时无法加载"
        description={resource.error}
        action={
          <Button
            onClick={() => void resource.reload()}
            icon={<ReloadOutlined />}
          >
            重试
          </Button>
        }
      />
    );
  if (resource.loading && !resource.data)
    return (
      <div className="loading-panel">
        <Spin tip="正在读取最新数据…">
          <div style={{ height: 100, width: 240 }} />
        </Spin>
      </div>
    );
  if (!resource.data || empty)
    return (
      <Card>
        <Empty description="当前范围内暂无数据" />
      </Card>
    );
  return <Spin spinning={resource.loading}>{children(resource.data)}</Spin>;
}
export const statusColors: Record<TaskStatus, string> = {
  draft: "default",
  pending_review: "processing",
  approved: "success",
  rejected: "error",
  paused: "warning",
  offline: "default",
};
export const StatusTag = ({ status }: { status: TaskStatus }) => (
  <Tag color={statusColors[status]} bordered={false}>
    {statusLabels[status] || status}
  </Tag>
);
export const ExecutionTag = ({ status }: { status: ExecutionStatus }) => (
  <Tag color={({ active: "processing", ended: "default", settled: "success", ...statusColors } as Record<ExecutionStatus, string>)[status]} bordered={false}>{executionLabels[status] || status}</Tag>
);
export const RefreshButton = ({ onClick }: { onClick: () => unknown }) => (
  <Button icon={<ReloadOutlined />} onClick={() => void onClick()}>
    刷新数据
  </Button>
);
export const SectionTitle = ({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) => (
  <div className="section-title">
    <div>
      {eyebrow && <div className="eyebrow">{eyebrow}</div>}
      <h1>{title}</h1>
      {description && <p>{description}</p>}
    </div>
    <Space wrap>{action}</Space>
  </div>
);
